import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Bell, User, Building2, Eye } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminNotificationsSimple = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log("Loading notifications from:", `${API}/api/v1/admin/notifications`);
      
      const response = await fetch(`${API}/api/v1/admin/notifications`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Loaded notifications:", data);
      
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error("Error loading notifications:", error);
      setError(error.message);
      // Set empty array to prevent infinite loading
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p>Caricamento notifiche...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-red-600 mb-4">Errore: {error}</p>
            <Button onClick={loadNotifications} variant="outline">
              Riprova
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-500" />
            Notifiche Admin ({notifications.length})
            {notifications.filter(n => !n.letta).length > 0 && (
              <Badge className="bg-red-500 text-white">
                {notifications.filter(n => !n.letta).length} non lette
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Lista Notifiche */}
      <div className="space-y-3">
        {notifications.length > 0 ? (
          notifications.map((notification, index) => (
            <Card key={notification.id || index} className="hover:shadow-md transition-all">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {/* Icona */}
                  <div className="flex-shrink-0 mt-1">
                    {notification.tipo === 'nuova_agenzia' ? (
                      <Building2 className="w-4 h-4 text-blue-500" />
                    ) : (
                      <User className="w-4 h-4 text-green-500" />
                    )}
                  </div>
                  
                  {/* Contenuto */}
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      {notification.titolo}
                    </h4>
                    <p className="text-sm text-gray-700 mb-2">
                      {notification.messaggio}
                    </p>
                    
                    {notification.entita_nome && (
                      <p className="text-sm text-blue-600 font-medium">
                        {notification.entita_nome}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 mt-2">
                      <Badge variant="outline">
                        {notification.tipo === 'nuova_agenzia' ? 'Agenzia' : 'Candidato'}
                      </Badge>
                      {!notification.letta && (
                        <Badge className="bg-red-100 text-red-700">
                          Non letto
                        </Badge>
                      )}
                    </div>
                    
                    {/* Metadata */}
                    {notification.metadata && (
                      <div className="bg-gray-50 p-2 rounded text-xs text-gray-600 mt-2">
                        {notification.metadata.citta && (
                          <span>📍 {notification.metadata.citta}</span>
                        )}
                        {notification.metadata.email && (
                          <span className="ml-3">✉️ {notification.metadata.email}</span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex-shrink-0">
                    <Button size="sm" variant="outline">
                      <Eye className="w-3 h-3 mr-1" />
                      Dettagli
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nessuna notifica
              </h3>
              <p className="text-gray-500">
                Le notifiche di sistema appariranno qui
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Debug Info */}
      <Card>
        <CardContent className="p-4 text-sm text-gray-600">
          <p>Debug: {notifications.length} notifiche caricate</p>
          <p>API: {API}/api/admin/notifications</p>
          <Button onClick={loadNotifications} size="sm" className="mt-2">
            Ricarica
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminNotificationsSimple;